<template>
    <ModalDialogContainer
        ref="modalContainer"
        v-model:display="displayStatus"
        :z-index="zIndex"
        :static="staticDialog"
        :lock-close="busy"
        :aria-label="currentStep.title || title"
        :class="{ 'modal-container-full-height': fullHeight }"
    >
        <div
            class="modal-dialog modal-md modal-box-container"
            :class="[`modal-box-${activeModalType}`, { 'modal-box-full-height': fullHeight }]"
            role="document"
            :style="{ maxWidth: (currentStep.maxWidth || maxWidth) + 'px' }"
            @click="stopPropagationEvent"
        >
            <div class="modal-action-container">
                <div v-if="!currentStep.onlyComponent" class="modal-header" :class="currentStep.positionIconHeader || positionIconHeader">
                    <div class="icon-container">
                        <span v-if="activeModalType === 'danger'" class="icon-spot">
                            <i class="fas fa-exclamation-triangle danger-icon"></i>
                        </span>
                        <span v-else-if="activeModalType === 'info'" class="icon-spot">
                            <i class="fas fa-info-circle info-icon"></i>
                        </span>
                        <span v-else-if="activeModalType === 'warning'" class="icon-spot">
                            <i class="fas fa-exclamation-triangle warning-icon"></i>
                        </span>
                        <span v-else class="icon-spot">
                            <i class="fas fa-check-circle success-icon"></i>
                        </span>
                    </div>
                    <div class="modal-header-content">
                        <div v-if="currentStep.title || title" class="modal-title">{{ currentStep.title || title }}</div>
                        <p v-if="currentStep.message || message" class="text-message-color">
                            <RichText :text="currentStep.message || message" />
                        </p>
                    </div>
                </div>

                <AlertBox
                    v-if="!currentStep.onlyComponent && (currentStep.alertType || alertType) !== 'none'"
                    :class="currentStep.alertPosition || alertPosition"
                    :alert-type="currentStep.alertType || alertType"
                    :alert-message="currentStep.alertMessage || alertMessage"
                />

                <div class="modal-body">
                    <component
                        :key="currentStepIndex"
                        v-if="currentStep.component || customComponent"
                        :is="currentStep.component || customComponent"
                        ref="contentComponent"
                        v-bind="mergedComponentProps"
                        @close-modal="close(true)"
                        @next-step="nextStep"
                        @prev-step="prevStep"
                        @go-to-step="goToStep"
                        @update-shared-data="updateSharedData"
                        @confirm="confirm(true)"
                    ></component>
                </div>

                <div v-if="currentStep.displayFooter ?? displayFooter" class="modal-footer">
                    <span v-if="!(currentStep.displayCloseButton ?? displayCloseButton) && !currentStep.onlyComponent" class="modal-footer">
                        <ActionButton
                            v-if="canGoBack"
                            class="action-modal-back-button"
                            :text="$t('Back')"
                            icon="fas fa-arrow-left"
                            icon-position="left"
                            style-type="neutral-outlined"
                            :disabled="busy"
                            @click="prevStep()"
                        />
                        <ActionButton :text="$t('Cancel')" style-type="neutral-outlined" :disabled="busy" @click="close()" />
                        <ActionButton
                            :text="currentStep.messageButton || messageButton || $t('Confirm')"
                            :icon="currentStep.iconButton || iconButton"
                            icon-position="left"
                            :style-type="confirmButtonStyle"
                            :disabled="busy"
                            @click="confirm()"
                        />
                    </span>
                    <ActionButton v-else :text="$t('Close')" style-type="neutral-outlined" :disabled="busy" @click="close()" />
                </div>
            </div>
        </div>
    </ModalDialogContainer>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from "vue";
import { useVModel } from "../../utils/v-model";
import { getUniqueStringId } from "@/utils/unique-id";
import { Request } from "@asanrom/request-browser";
import AlertBox from "@/components/ui/AlertBox.vue";
import RichText from "@/components/ui/RichText.vue";
import ActionButton from "@/components/buttons/ActionButton.vue";
import type { ActionModalStep, ActionModalType } from "@/types/action-modal";
import type { ButtonStyleType } from "@/types/buttons";

export default defineComponent({
    components: {
        AlertBox,
        RichText,
        ActionButton,
    },
    name: "ActionModal",
    emits: ["update:display", "confirm"],
    props: {
        display: Boolean,
        onlyComponent: {
            type: Boolean,
            default: false,
        },
        modalType: {
            type: String as PropType<ActionModalType>,
            default: "danger",
        },
        title: String,
        message: String,
        messageButton: String,
        iconButton: {
            type: String,
            default: "fas fa-check",
        },
        alertType: {
            type: String as PropType<"alert-info" | "alert-success" | "alert-danger" | "alert-warning" | "none">,
            default: "none",
        },
        alertPosition: {
            type: String as PropType<"top" | "middle" | "bottom">,
            default: "top",
        },
        alertMessage: {
            type: String,
            default: "Are you sure you want to proceed? This action cannot be undone.",
        },
        customComponent: {
            type: Object,
            default: null,
        },
        componentProps: {
            type: Object,
            default: () => ({}),
        },
        positionIconHeader: {
            type: String as PropType<"left" | "center" | "right" | "none">,
            default: "left",
        },
        displayFooter: {
            type: Boolean,
            default: true,
        },
        displayCloseButton: {
            type: Boolean,
            default: false,
        },
        steps: {
            type: Array as PropType<ActionModalStep[]>,
            default: () => [],
        },
        maxWidth: {
            type: Number as PropType<number | undefined>,
            default: 520,
        },
        fullHeight: {
            type: Boolean,
            default: false,
        },
        staticDialog: {
            type: Boolean,
            default: false,
        },
        zIndex: {
            type: Number as PropType<number | undefined>,
            default: undefined,
        },
    },

    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
            deleteRequestId: getUniqueStringId(),
            contentComponent: ref<{ busy?: boolean } | null>(null),
        };
    },

    data() {
        return {
            confirmation: "",
            currentStepIndex: 0,
            stepHistory: [] as number[],
            sharedStepData: {} as Record<string, any>,
        };
    },

    computed: {
        busy(): boolean {
            return !!this.contentComponent?.busy;
        },

        activeModalType(): ActionModalType {
            return this.currentStep.modalType || this.modalType;
        },

        confirmButtonStyle(): ButtonStyleType {
            return this.activeModalType === "danger" ? "delete-filled" : "primary-brand-filled";
        },

        currentStep(): ActionModalStep {
            if (this.steps.length > 0) {
                return this.steps[this.currentStepIndex];
            }
            return {
                title: this.title,
                message: this.message,
                modalType: this.modalType,
                component: this.customComponent,
                componentProps: this.componentProps,
                alertType: this.alertType,
                alertPosition: this.alertPosition,
                alertMessage: this.alertMessage,
                positionIconHeader: this.positionIconHeader,
                displayFooter: this.displayFooter,
                displayCloseButton: this.displayCloseButton,
                onlyComponent: this.onlyComponent,
                messageButton: this.messageButton,
                iconButton: this.iconButton,
                maxWidth: this.maxWidth,
            };
        },

        canGoBack(): boolean {
            return this.stepHistory.length > 0;
        },

        availableStepIds(): string[] {
            return this.steps.map((step, index) => step.id || `step-${index}`).filter((id) => id !== this.currentStepId);
        },

        currentStepId(): string {
            return this.currentStep.id || `step-${this.currentStepIndex}`;
        },

        mergedComponentProps(): any {
            return {
                ...(this.currentStep.componentProps || this.componentProps),
                stepNavigation: {
                    currentStepId: this.currentStepId,
                    currentStepIndex: this.currentStepIndex,
                    totalSteps: this.steps.length,
                    availableStepIds: this.availableStepIds,
                    canGoBack: this.canGoBack,
                    canGoNext: this.currentStepIndex < this.steps.length - 1,
                },
                sharedData: this.sharedStepData,
            };
        },
    },
    methods: {
        // `fromContent` marks a close asked for by the content itself, which happens once its
        // request already finished. That is not a user gesture, so neither the guard below nor the
        // lock of the container must swallow it, or the modal stays open and unclosable
        close: function (fromContent?: boolean) {
            if (this.busy && !fromContent) {
                return;
            }
            this.currentStepIndex = 0;
            this.stepHistory = [];
            this.sharedStepData = {};
            // The content can finish and confirm in the same tick. Let lockClose receive
            // the new busy state before asking the container to close.
            this.$nextTick(() => this.$closeModal(fromContent));
        },

        updateSharedData: function (data: Record<string, any>) {
            this.sharedStepData = { ...this.sharedStepData, ...data };
        },

        nextStep: function () {
            if (this.currentStepIndex < this.steps.length - 1) {
                this.stepHistory.push(this.currentStepIndex);
                this.currentStepIndex++;
            }
        },

        prevStep: function (stepIdOrIndex?: string | number) {
            if (stepIdOrIndex !== undefined) {
                this.navigateToStep(stepIdOrIndex);
                return;
            }
            if (this.stepHistory.length > 0) {
                this.currentStepIndex = this.stepHistory.pop()!;
            }
        },

        goToStep: function (stepIdOrIndex: string | number) {
            this.stepHistory.push(this.currentStepIndex);
            this.navigateToStep(stepIdOrIndex);
        },

        navigateToStep: function (stepIdOrIndex: string | number) {
            if (typeof stepIdOrIndex === "number") {
                if (stepIdOrIndex >= 0 && stepIdOrIndex < this.steps.length) {
                    this.currentStepIndex = stepIdOrIndex;
                } else {
                    console.warn(`[ActionModal] Invalid step index: ${stepIdOrIndex}`);
                }
            } else {
                const index = this.steps.findIndex((step, i) => (step.id || `step-${i}`) === stepIdOrIndex);
                if (index !== -1) {
                    this.currentStepIndex = index;
                } else {
                    console.warn(`[ActionModal] Step not found: ${stepIdOrIndex}`);
                }
            }
        },

        stopPropagationEvent: function (e) {
            e.stopPropagation();
        },

        confirm: function (fromContent?: boolean) {
            if (this.busy && !fromContent) {
                return;
            }
            const data = { ...this.sharedStepData };
            this.close(fromContent);
            this.$emit("confirm", data);
        },
    },
    mounted: function () {
        if (this.display) {
            this.$autoFocus();
        }
    },
    beforeUnmount: function () {
        Request.Abort(this.deleteRequestId);
    },
    watch: {
        display(newVal) {
            if (newVal) {
                this.currentStepIndex = 0;
                this.stepHistory = [];
                this.$autoFocus();
            }
        },
    },
});
</script>

<style scoped>
@import "@/style/components/action-modal.css";
</style>
